import { readAndClearPendingSkillRemix } from "@promptlayer/ui";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { API_URL } from "@/api/application-api";
import { SaveEntityToLocationControlled } from "@/components/SaveEntityToLocation";
import { useAuth } from "@/context/auth-context";
import { EntityType } from "@/queries";
import { useCreateSkillCollection } from "@/queries/skill-collections";
import { displayErrorToast } from "@/utils/toast";

function extractErrorMessage(data: unknown, fallback: string): string {
  if (!data || typeof data !== "object") {
    return fallback;
  }
  const obj = data as Record<string, unknown>;
  const raw = obj.message;

  if (Array.isArray(raw)) {
    const joined = raw
      .map((e: unknown) =>
        e && typeof e === "object" && "msg" in e
          ? String((e as Record<string, unknown>).msg)
          : JSON.stringify(e),
      )
      .join("; ");
    return joined || fallback;
  }

  if (typeof raw === "string" && raw.trim()) {
    return raw.trim();
  }

  return fallback;
}

interface SharedSkillCollectionResponse {
  success: boolean;
  skill_collection: {
    name: string;
    provider: string | null;
  };
  version: {
    number: number;
    release_label: string | null;
  };
  files: Record<string, string>;
}

export const SkillRemixHandler = () => {
  const [skillData, setSkillData] = useState<SharedSkillCollectionResponse | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [defaultName, setDefaultName] = useState("");

  const authContext = useAuth();
  const createSkillCollectionMutation = useCreateSkillCollection();
  const { workspaceId } = useParams();

  useEffect(() => {
    const pendingRemix = readAndClearPendingSkillRemix();
    if (!pendingRemix) {
      return;
    }

    const userToken = authContext?.userToken;

    fetch(`${API_URL}/api/sessions/${pendingRemix.share_token}`, {
      credentials: "include",
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
    })
      .then(async configRes => {
        const configData: unknown = await configRes.json().catch(() => null);

        if (
          configRes.ok &&
          configData &&
          typeof configData === "object" &&
          (configData as Record<string, unknown>).success &&
          (configData as Record<string, unknown>).session
        ) {
          const session = (configData as Record<string, unknown>).session as Record<
            string,
            unknown
          >;

          if (session.resource_type !== "skill_collection") {
            displayErrorToast("This shared deployment is not a skill collection.");
            return;
          }

          if (!session.remix_enabled) {
            displayErrorToast("Remixing is not enabled for this session.");
            return;
          }
        } else {
          displayErrorToast(
            extractErrorMessage(configData, "This shared session is no longer available."),
          );
          return;
        }

        return fetch(`${API_URL}/api/sessions/${pendingRemix.share_token}/skills`, {
          credentials: "include",
          headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
        });
      })
      .then(response => {
        if (!response) {
          return;
        }
        return response.json().then((data: unknown) => ({ response, data }));
      })
      .then(result => {
        if (!result) {
          return;
        }
        const { response, data } = result;
        if (
          response.ok &&
          data &&
          typeof data === "object" &&
          (data as Record<string, unknown>).success &&
          (data as Record<string, unknown>).skill_collection &&
          (data as Record<string, unknown>).files
        ) {
          const parsed = data as SharedSkillCollectionResponse;
          setDefaultName(parsed.skill_collection.name);
          setSkillData(parsed);
          setDialogOpen(true);
          return;
        }

        displayErrorToast("This shared skill collection is no longer available.");
      })
      .catch(() => {
        displayErrorToast("Could not load the shared skill collection for remixing.");
      });
    // authContext is stable for the lifetime of the session — safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (name: string, folderId: number | null) => {
    if (!skillData) {
      return;
    }

    if (!workspaceId) {
      displayErrorToast("Could not determine your workspace. Please try again.");
      return;
    }

    const versionDescriptor = skillData.version.release_label
      ? `label ${skillData.version.release_label}`
      : `version ${skillData.version.number}`;

    await createSkillCollectionMutation.mutateAsync({
      workspaceId: Number(workspaceId),
      name,
      folderId,
      provider: skillData.skill_collection.provider,
      files: Object.entries(skillData.files).map(([path, content]) => ({
        path,
        content,
      })),
      commitMessage: `Remixed from shared skill collection (${versionDescriptor})`,
    });
  };

  if (!dialogOpen) {
    return null;
  }

  return (
    <SaveEntityToLocationControlled
      open={dialogOpen}
      onOpenChange={setDialogOpen}
      type={EntityType.SKILL_COLLECTION}
      onSave={handleSave}
      defaultName={defaultName}
      title="Remix Skill Collection"
      description="Choose a name and location for the remixed skill collection."
      saveButtonText="Remix"
    />
  );
};
